import React, { useState } from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import { Guest } from 'types/reservations';
import TextField from 'Components/Atoms/TextField';
import Button from 'Components/Atoms/Button';
import { PersonAdd } from 'Components/Atoms/Icons';
import Typography from 'Components/Atoms/Typography';
import { noop } from 'utils/helper';
import useGuestAutoComplete from 'CustomHooks/useGuestAutoComplete';
import useGuestbookVersion from 'CustomHooks/useGuestbookVersion';
import useRestaurant from 'CustomHooks/useRestaurant';

export type GuestDropdownProps = {
  value: null | Guest;
  onChange: (guest: Guest) => void;
};

const EMPTY_GUEST: Guest = {
  name: '',
  guestId: '',
  phone: '',
  email: '',
  hostComment: '',
  comment: '',
  attr: [],
};

const isPhoneNumber = (input: string) =>
  /^\+?\d{6,}$/.test(input.replace(/[\s/()-]/g, ''));

const toInternational = (input: string, country: string) => {
  const cleaned = input.replace(/[\s/()-]/g, '');

  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.startsWith('00')) return `+${cleaned.slice(2)}`;

  return `${country === 'at' ? '+43' : '+49'}${cleaned.replace(/^0/, '')}`;
};

const GuestDropdownV01 = ({ value, onChange = noop }: GuestDropdownProps) => {
  const { version } = useGuestbookVersion();
  const { experimentalFlags } = useRestaurant();
  const [searchInput, setSearchInput] = useState('');

  const { guests: searchResults } = useGuestAutoComplete({
    search: searchInput || '',
    country: experimentalFlags?.data?.phoneDefaultCountry || 'de',
  });

  const isSelected = !!(value?.id || value?.createNew);

  const handleSearchChange = (e: any) => {
    const val = e.target.value;
    setSearchInput(val);
    onChange({ ...EMPTY_GUEST, name: val, createNew: false });
  };

  const handleNewGuest = () => {
    const isPhone = isPhoneNumber(searchInput);
    const country = experimentalFlags?.data?.phoneDefaultCountry || 'de';

    onChange({
      ...EMPTY_GUEST,
      name: isPhone ? '' : searchInput,
      phone: isPhone ? toInternational(searchInput, country) : '',
      createNew: true,
    });
  };

  const handleGuestClick = (id: string) => {
    const guest = searchResults.find((g) => id === g.id);
    if (guest) {
      setSearchInput(guest.name || '');
      onChange({ ...EMPTY_GUEST, ...guest });
    }
  };

  const handleClear = () => {
    setSearchInput('');
    onChange({ ...EMPTY_GUEST });
  };

  if (isSelected && !value?.createNew) {
    // Existing guest selected — show as chip
    return (
      <Box className="GuestDropdown">
        <Box
          className="guest-selected"
          onClick={handleClear}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderRadius: 4,
            cursor: 'pointer',
            border: '1px solid var(--color-primary)',
            background: 'var(--color-paper)',
          }}
        >
          <Box>
            <Typography variant="text-3" color="primary">
              {value?.name || 'Gast'}
            </Typography>
            {value?.phone && (
              <Typography variant="text-4" color="subdued" block>
                {value.phone}
                {value.email ? ` | ${value.email}` : ''}
              </Typography>
            )}
          </Box>
          <Typography variant="text-4" color="subdued">
            ✕
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isSelected && value?.createNew) {
    // New guest — editable name field with option to go back to search
    return (
      <Box className="GuestDropdown">
        <TextField
          value={value?.name || ''}
          onChange={(e: any) => {
            onChange({ ...value, name: e.target.value, createNew: true });
          }}
          fullWidth
          required
          label="Name of the guest"
          labelTranslation="reservations"
          InputLabelProps={{ shrink: true }}
        />
        <Typography
          variant="text-4"
          color="primary"
          translation="reservations"
          onClick={handleClear}
          style={{ cursor: 'pointer', marginTop: 4 }}
        >
          Search existing guest
        </Typography>
      </Box>
    );
  }

  const placeholder =
    version === 'v02'
      ? 'Enter name, email or phone number'
      : 'Enter name';

  return (
    <Box className="GuestDropdown">
      <TextField
        value={searchInput}
        onChange={handleSearchChange}
        fullWidth
        required
        label="Guest"
        labelTranslation="reservations"
        placeholder={placeholder}
        InputLabelProps={{ shrink: true }}
      />

      <Box
        className="guest-results"
        style={{ maxHeight: 220, overflow: 'auto', marginTop: 4 }}
      >
        {searchResults.map((res) => (
          <Box
            key={res.id}
            padding="sm"
            underline
            onClick={() => handleGuestClick(res?.id || '')}
            className="guest"
          >
            <Typography block variant="text-3">
              {res.name}
              {res.attr?.includes('vip') && ' (VIP)'}
              {res.attr?.includes('regular') && ' (Stammgast)'}
            </Typography>
            <Typography block variant="text-4" color="subdued">
              {res.phone}
              {res.email ? ` | ${res.email}` : ''}
            </Typography>
            {((res as any).visits > 0 || (res as any).lastReservation) && (
              <Typography block variant="text-4" color="subdued">
                {(res as any).visits > 0
                  ? `${(res as any).visits} Reservierung${(res as any).visits !== 1 ? 'en' : ''}`
                  : ''}
                {(res as any).lastReservation
                  ? ` | Letzter Besuch: ${new Date((res as any).lastReservation).toLocaleDateString()}`
                  : ''}
              </Typography>
            )}
          </Box>
        ))}
      </Box>

      <Box style={{ marginTop: 8 }}>
        <Button
          fullWidth
          endIcon={(p) => <PersonAdd {...p} />}
          onClick={handleNewGuest}
          variant="transparent"
          translation="reservations"
        >
          New Guest
        </Button>
      </Box>
    </Box>
  );
};

const GuestDropdown = (params: GuestDropdownProps) => {
  return <GuestDropdownV01 {...params} />;
};

export default GuestDropdown;
